// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Asset exception report page with tabs for date and detail inconsistencies.
 *
 * Displays assets with data inconsistencies that need to be resolved, such as
 * dates that don't align properly or missing/incorrect detail information.
 */
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { PageContent } from '@/components/page-content';
import { SupportSnackbar } from '@/components/support-snackbar';
import { exceptionTypeSchema } from '@/constants/asset-exception-type';
import { DownloadRounded } from '@/constants/hardfin-standard-icons';
import { DateInconsistenciesTable } from '@/report/components/date-inconsistencies-table';
import { DetailInconsistenciesTable } from '@/report/components/detail-inconsistencies-table';
import { ExceptionReportLoader } from '@/report/components/exception-report-loader';
import { ExceptionSummaryCard } from '@/report/components/exception-summary-card';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import { useDateInconsistencies } from '@/report/hooks/use-date-inconsistencies';
import { useDetailInconsistencies } from '@/report/hooks/use-detail-inconsistencies';
import {
  exportDateInconsistenciesCSV,
  exportDetailInconsistenciesCSV,
} from '@/report/utilities/export-exception-csv';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

const REPORT_TITLE = 'Asset exception report';

type ExceptionTabType = 'date' | 'detail';

const exceptionTabTypes: ExceptionTabType[] = ['date', 'detail'];

/**
 * Get the description for each exception tab type
 */
function getExceptionDescription(tabType: ExceptionTabType): React.ReactNode {
  switch (tabType) {
    case 'date':
      return (
        <>
          <strong>Date inconsistencies</strong> occur when asset activity is
          recorded before the asset's inventory date, or when lifecycle dates
          are out of order (e.g., in-service date before inventory date).
        </>
      );
    case 'detail':
      return (
        <>
          <strong>Detail inconsistencies</strong> occur when required
          information is missing or values don't make sense (e.g., missing cost
          basis, salvage value exceeding cost basis).
        </>
      );
    default:
      return '';
  }
}

const assetExceptionSearchSchema = z.object({
  exceptionType: exceptionTypeSchema.optional(),
  assetIds: z.array(z.string()).optional(),
});

export const Route = createFileRoute('/_app/reports/exception/asset/$tabType')({
  component: AssetExceptionReportRoute,
  validateSearch: assetExceptionSearchSchema,
});

function AssetExceptionReportRoute() {
  const search = Route.useSearch();
  const params = Route.useParams();
  const navigate = Route.useNavigate();

  const [isDateAnimationComplete, setIsDateAnimationComplete] = useState(false);
  const [isDetailAnimationComplete, setIsDetailAnimationComplete] =
    useState(false);

  // Export state for showing spinner during CSV generation
  const [isExporting, setIsExporting] = useState(false);

  // Only fire the query for the active tab to avoid unnecessary API calls and parsing
  const dateInconsistenciesQuery = useDateInconsistencies({
    exceptionType: search.exceptionType,
    assetIds: search.assetIds,
    enabled: params.tabType === 'date',
  });

  const detailInconsistenciesQuery = useDetailInconsistencies({
    exceptionType: search.exceptionType,
    assetIds: search.assetIds,
    enabled: params.tabType === 'detail',
  });

  // Create a key based on query parameters to track unique data sets
  const dateQueryKey = `${search.exceptionType || ''}-${search.assetIds?.join(',') || ''}-${dateInconsistenciesQuery.data?.totalAnalyzed || 0}`;
  const detailQueryKey = `${search.exceptionType || ''}-${search.assetIds?.join(',') || ''}-${detailInconsistenciesQuery.data?.totalAnalyzed || 0}`;

  // Track which tab+queryKey combinations have completed animation
  // Format: "date-{queryKey}" or "detail-{queryKey}"
  const [completedTabQueries, setCompletedTabQueries] = useState<Set<string>>(
    new Set()
  );

  // Initialize animation completion state based on current tab
  useEffect(() => {
    if (params.tabType === 'date') {
      const dateCompletionKey = `date-${dateQueryKey}`;
      if (completedTabQueries.has(dateCompletionKey)) {
        setIsDateAnimationComplete(true);
      } else if (dateInconsistenciesQuery.data?.totalAnalyzed !== undefined) {
        setIsDateAnimationComplete(false);
      }
    } else {
      const detailCompletionKey = `detail-${detailQueryKey}`;
      if (completedTabQueries.has(detailCompletionKey)) {
        setIsDetailAnimationComplete(true);
      } else if (detailInconsistenciesQuery.data?.totalAnalyzed !== undefined) {
        setIsDetailAnimationComplete(false);
      }
    }
  }, [
    params.tabType,
    dateQueryKey,
    detailQueryKey,
    dateInconsistenciesQuery.data?.totalAnalyzed,
    detailInconsistenciesQuery.data?.totalAnalyzed,
    completedTabQueries,
  ]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const newTabType = exceptionTabTypes[newValue];

    if (newTabType) {
      navigate({
        search: {
          exceptionType: search.exceptionType,
          assetIds: search.assetIds,
        },
        params: { tabType: newTabType },
      });
    }
  };

  /**
   * Handles exporting the current exception data to CSV.
   * Calls the appropriate export function based on the current tab type.
   * Shows a spinner during export for visual feedback.
   */
  const handleExportCSV = () => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);

    // Use setTimeout to allow the UI to update before the synchronous export
    setTimeout(() => {
      try {
        if (params.tabType === 'date') {
          if (dateInconsistenciesQuery.data?.assets) {
            exportDateInconsistenciesCSV(dateInconsistenciesQuery.data.assets);
          }
        } else {
          if (detailInconsistenciesQuery.data?.assets) {
            exportDetailInconsistenciesCSV(
              detailInconsistenciesQuery.data.assets
            );
          }
        }
      } finally {
        setIsExporting(false);
      }
    }, 0);
  };

  /**
   * Returns true if the current tab has data available for export.
   */
  const canExport = (): boolean => {
    if (params.tabType === 'date') {
      return (
        !dateInconsistenciesQuery.isLoading &&
        (dateInconsistenciesQuery.data?.assets.length ?? 0) > 0
      );
    }
    return (
      !detailInconsistenciesQuery.isLoading &&
      (detailInconsistenciesQuery.data?.assets.length ?? 0) > 0
    );
  };

  const currentTabIndex = params.tabType === 'date' ? 0 : 1;

  return (
    <PageContent
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SupportSnackbar
        isOpen={
          dateInconsistenciesQuery.isError || detailInconsistenciesQuery.isError
        }
        onClose={() => {
          if (params.tabType === 'date') {
            dateInconsistenciesQuery.refetch();
          } else {
            detailInconsistenciesQuery.refetch();
          }
        }}
        message="An error occurred while loading the asset exception report."
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack>
          <ReportBreadcrumbs title={REPORT_TITLE} parent="accounting" />
          <Typography variant="h5">{REPORT_TITLE}</Typography>
        </Stack>
        <ConditionalTooltip
          arrow
          placement="left"
          tooltipText={canExport() ? undefined : 'No data available to export'}
        >
          <Button
            variant="outlined"
            size="small"
            endIcon={
              isExporting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DownloadRounded fontSize="small" />
              )
            }
            onClick={handleExportCSV}
            disabled={!canExport() || isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export exceptions'}
          </Button>
        </ConditionalTooltip>
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginTop: 2 }}>
        <Tabs value={currentTabIndex} onChange={handleTabChange}>
          <Tab label="Date inconsistencies" />
          <Tab label="Detail inconsistencies" />
        </Tabs>
      </Box>

      <Alert severity="info" sx={{ marginY: 2 }}>
        {getExceptionDescription(params.tabType as ExceptionTabType)}
      </Alert>

      {/* Date tab content */}
      {params.tabType === 'date' && (
        <>
          {/* Show loader while data is loading */}
          {dateInconsistenciesQuery.isLoading && (
            <ExceptionReportLoader reportType="date" isDataReady={false} />
          )}

          {/* Show summary card once data is ready (runs animation) */}
          {!dateInconsistenciesQuery.isLoading && (
            <Box sx={{ marginBottom: 2 }}>
              <ExceptionSummaryCard
                stats={{
                  totalAnalyzed:
                    dateInconsistenciesQuery.data?.totalAnalyzed ?? 0,
                  withExceptions:
                    dateInconsistenciesQuery.data?.assets.length ?? 0,
                }}
                isLoading={false}
                skipAnimation={isDateAnimationComplete}
                onAnimationComplete={() => {
                  setIsDateAnimationComplete(true);
                  setCompletedTabQueries((prev) =>
                    new Set(prev).add(`date-${dateQueryKey}`)
                  );
                }}
              />
            </Box>
          )}

          {/* Show table after summary card animation completes */}
          {!dateInconsistenciesQuery.isLoading && isDateAnimationComplete && (
            <DateInconsistenciesTable
              assets={dateInconsistenciesQuery.data?.assets || []}
            />
          )}
        </>
      )}

      {/* Detail tab content */}
      {params.tabType === 'detail' && (
        <>
          {/* Show loader while data is loading */}
          {detailInconsistenciesQuery.isLoading && (
            <ExceptionReportLoader reportType="detail" isDataReady={false} />
          )}

          {/* Show summary card once data is ready (runs animation) */}
          {!detailInconsistenciesQuery.isLoading && (
            <Box sx={{ marginBottom: 2 }}>
              <ExceptionSummaryCard
                stats={{
                  totalAnalyzed:
                    detailInconsistenciesQuery.data?.totalAnalyzed ?? 0,
                  withExceptions:
                    detailInconsistenciesQuery.data?.assets.length ?? 0,
                }}
                isLoading={false}
                skipAnimation={isDetailAnimationComplete}
                onAnimationComplete={() => {
                  setIsDetailAnimationComplete(true);
                  setCompletedTabQueries((prev) =>
                    new Set(prev).add(`detail-${detailQueryKey}`)
                  );
                }}
              />
            </Box>
          )}

          {/* Show table after summary card animation completes */}
          {!detailInconsistenciesQuery.isLoading &&
            isDetailAnimationComplete && (
              <DetailInconsistenciesTable
                assets={detailInconsistenciesQuery.data?.assets || []}
              />
            )}
        </>
      )}
    </PageContent>
  );
}
